import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Observable, of, Subject } from "rxjs";
import { distinctUntilChanged, takeUntil, tap } from "rxjs/operators";
import { CameraComponent } from "src/app/annotation/camera/camera.component";
import { MainDistributionEnum } from "src/app/shared/Models/enums/MainDistributionEnum";
import { MainDistributionFrontYardPavement } from "src/app/shared/Models/enums/MainDistributionFrontYardPavement";
import { MainDistributionUndergroundReason } from "src/app/shared/Models/enums/MainDistributionUndergroundReason";
import { OtherObstructionType } from "src/app/shared/Models/enums/OtherObstructionType";
import { StreetFurnitureType } from "src/app/shared/Models/enums/StreetFurnitureType";
import { StreetPavingTypeEnum } from "src/app/shared/Models/enums/StreetPavingTypeEnum";
import { SurveyPhotoTypeEnum } from "src/app/shared/Models/enums/SurveyPhotoTypeEnum";
import { Survey } from "src/app/shared/Models/read/Survey";
import { FacadeDetailPhotoRemarksDto } from "src/app/shared/Models/write/facadeDetailPhotoRemarks.model";
import { ToastService } from "src/app/shared/toast.service";
import { PictureInfo } from "../../shared/components/photo-and-remark/photo-viewmodel";
import { NumericalService } from "../../shared/services/numerical.service";
import { SurveyType } from "../../shared/services/photo.service";
import { SavingStateService } from "../../shared/services/saving-state.service";
import {
  OutsideInfoEnum,
  OUTSIDE_SURVEY_OUTSIDE_INFO,
} from "../config/outside-survey.magic-strings";
import { FormValidationService } from "../../shared/services/formValidation.service";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";
import { merge, isEqual } from "lodash";

const ATTRIBUTES = "attributes";
@Component({
  selector: "app-outside-survey-technical",
  templateUrl: "./outside-survey-technical.component.html",
  styleUrls: ["./outside-survey-technical.component.scss"],
})
export class OutsideSurveyTechnicalComponent implements OnInit, OnDestroy {
  tabId = OUTSIDE_SURVEY_OUTSIDE_INFO;
  @Input() survey: Survey;
  @Input() isAuthCanWrite: boolean;
  @Input() cameraLayer: HTMLDivElement;
  @Input() camera: CameraComponent;
  mainDistributionEnum = MainDistributionEnum;
  mainDistributionUndergroundReason = MainDistributionUndergroundReason;
  mainDistributionFrontYardPavement = MainDistributionFrontYardPavement;
  streetPavingTypeEnum = StreetPavingTypeEnum;
  questions: any = {};
  remarks: any = {};
  technicalForm: FormGroup;
  selectedFrontYardPhoto: any;
  surveyType = SurveyType;
  surveyPhotoType = SurveyPhotoTypeEnum;
  BuildingTypeEnum = BuildingTypeEnum;
  solutionDoubtPhotos: PictureInfo[] = [];
  frontYardPhotos: PictureInfo[] = [];
  frontYardPhotoRemarksOnInit = [];
  solutionDoubtPhotosOnInit = [];
  outsideInfoEnum = OutsideInfoEnum;

  mainDistributionTypeOptions = [
    {
      displayName: $localize`:@@survey.inside.outside.technicalinfo.distrubution.type.underground:Underground`,
      value: this.mainDistributionEnum.Underground,
    },
    {
      displayName: $localize`:@@survey.inside.outside.technicalinfo.distrubution.type.facade:Facade`,
      value: this.mainDistributionEnum.Facade,
    },
  ];

  mainDistributionUndergroundReasonOptions = [
    {
      value: MainDistributionUndergroundReason.Irregular,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.irregular:Irregular`,
    },
    {
      value: MainDistributionUndergroundReason.Monument,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.monument:Monument`,
    },
    {
      value: MainDistributionUndergroundReason.Glass,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.glass:Glass`,
    },
    {
      value: MainDistributionUndergroundReason.Gable,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.gable:Gable`,
    },
    {
      value: MainDistributionUndergroundReason.Height,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.height:Height`,
    },

    {
      value: MainDistributionUndergroundReason.Ornamental,
      label: $localize`:@@survey.inside.outside.technicalinfo.distribution.undergroundReason.ornamental:Ornamental`,
    },
  ];

  mainDistributionFrontYardPavementOptions = [
    {
      value: MainDistributionFrontYardPavement.FullGround,
      label: $localize`:@@enum.frontyard.pavement.fullground:Full Ground`,
    },
    {
      value: MainDistributionFrontYardPavement.StonePavement,
      label: $localize`:@@enum.frontyard.pavement.stonePavement:Stone Pavement`,
    },
    {
      value: MainDistributionFrontYardPavement.Asphalt,
      label: $localize`:@@enum.frontyard.pavement.asphalt:Asphalt`,
    },
    {
      value: MainDistributionFrontYardPavement.Concrete,
      label: $localize`:@@enum.frontyard.pavement.concrete:Concrete`,
    },
    {
      value: MainDistributionFrontYardPavement.OrnamentalPavement,
      label: $localize`:@@enum.frontyard.pavement.tiles.ornamental:Ornamental Pavement`,
    },
    {
      value: MainDistributionFrontYardPavement.Cobblestones,
      label: $localize`:@@enum.frontyard.pavement.tiles.cobblestones:Cobblestones`,
    },
    {
      value: MainDistributionFrontYardPavement.PebbleStone,
      label: $localize`:@@enum.frontyard.pavement.tiles.pebbleStone:Pebble-stone`,
    },
    {
      value: MainDistributionFrontYardPavement.Other,
      label: $localize`:@@enum.frontyard.pavement.other:Other`,
    },
  ];

  streetFurnitureTypeOptions = [
    {
      value: StreetFurnitureType.Tree,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.tree:Tree`,
    },
    {
      value: StreetFurnitureType.StreetLight,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.lamppost:Lamppost`,
    },
    {
      value: StreetFurnitureType.Pole,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.pole:Pole`,
    },
    {
      value: StreetFurnitureType.ParkingMeter,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.parkingmeter:Parking meter`,
    },
    {
      value: StreetFurnitureType.RoadSign,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.roadsign:Road sign`,
    },
    {
      value: StreetFurnitureType.BusStop,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.busstop:Bus stop`,
    },
    {
      value: StreetFurnitureType.FootPath,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.footpath:Foot path`,
    },
    {
      value: StreetFurnitureType.Other,
      label: $localize`:@@survey.inside.outside.technicalinfo.streetfurnituretype.other:Other`,
    },
  ];
  otherObstructionTypeOptions = [
    {
      value: OtherObstructionType.Billboard,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.billboard:Billboard`,
    },
    {
      value: OtherObstructionType.CrepeWithInsulation,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.crepeWithInsulation:Crepe with insulation`,
    },
    {
      value: OtherObstructionType.Sunshade,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.sunshade:Sun shade`,
    },
    {
      value: OtherObstructionType.Terrace,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.terrace:Terrace`,
    },
    {
      value: OtherObstructionType.Lighting,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.lighting:Lighting`,
    },
    {
      value: OtherObstructionType.Other,
      label: $localize`:@@survey.inside.outside.technicalinfo.otherobstructiontype.other:Other`,
    },
  ];

  pavingFootpathTypeOptions = [
    {
      value: StreetPavingTypeEnum.FullGround,
      label: $localize`:@@enum.frontyard.pavement.fullground:Full Ground`,
    },
    {
      value: StreetPavingTypeEnum.StonePavement,
      label: $localize`:@@enum.frontyard.pavement.stonePavement:Stone Pavement`,
    },
    {
      value: StreetPavingTypeEnum.Asphalt,
      label: $localize`:@@enum.frontyard.pavement.asphalt:Asphalt`,
    },
    {
      value: StreetPavingTypeEnum.Concrete,
      label: $localize`:@@enum.frontyard.pavement.concrete:Concrete`,
    },
    {
      value: StreetPavingTypeEnum.OrnamentalPavement,
      label: $localize`:@@enum.frontyard.pavement.tiles.ornamental:Ornamental Pavement`,
    },
    {
      value: StreetPavingTypeEnum.Cobblestones,
      label: $localize`:@@enum.frontyard.pavement.tiles.cobblestones:Cobblestones`,
    },
    {
      value: StreetPavingTypeEnum.PebbleStone,
      label: $localize`:@@enum.frontyard.pavement.tiles.pebbleStone:Pebble-stone`,
    },
    {
      value: StreetPavingTypeEnum.Other,
      label: $localize`:@@enum.frontyard.pavement.other:Other`,
    },
  ];

  constructor(
    private toastService: ToastService,
    private fb: FormBuilder,
    private _numService: NumericalService, // is used in HTML Do Not remove
    private _savingStateService: SavingStateService,
    private formValidationService: FormValidationService
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.frontYardPhotos = this.survey.photos.frontYardPhotos;
    this.solutionDoubtPhotos = this.survey.photos.solutionDoubtPhotos;
    this.technicalForm = new FormGroup({
      mainDistribution: new FormControl(
        this.survey.mainDistribution,
        Validators.required
      ),
      streetPavingType: new FormControl(
        this.survey.pavingFootpath,
        Validators.required
      ),
      mainDistributionUndergroundReason: new FormControl(
        this.survey.mainDistributionUndergroundReason,
        Validators.required
      ),
      mainDistributionFrontYardLength: new FormControl(
        this.survey.mainDistributionFrontYardLength,
        [Validators.required, Validators.pattern("^[0-9]*$")]
      ),
      mainDistributionFrontYardPavement: new FormControl(
        this.survey.mainDistributionFrontYardPavement,
        [Validators.required]
      ),
    });
    this.technicalForm
      .get("mainDistribution")
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((mainDistribution) =>
        this.onChangeMainDistribution(mainDistribution)
      );
    this.onChangeMainDistribution(
      this.technicalForm.get("mainDistribution").value
    );
    this.setQuestions();
    this.toggleFrontYardItemsVisibility(this.survey.frontYard);
    this.toggleStreetFurnitureItemsVisibility(this.survey.streetFurniture);
    this.toggleOtherObstructionItemsVisibility(this.survey.otherObstruction);
    this.togglesolutionDoubtItemsVisibility(this.survey.solutionDoubt);
    this.formValidationService.addFormError([OUTSIDE_SURVEY_OUTSIDE_INFO], {
      [OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE]: {
        [OutsideInfoEnum.TYPE_FIELD]: false,
        [OutsideInfoEnum.UNDERGROUND_REASON_FIELD]: false,
        [OutsideInfoEnum.FACADE_HEIGHT_FIELD]: false,
        [OutsideInfoEnum.FRONT_YARD_FIELD]: false,
        [OutsideInfoEnum.FRONT_YARD_LENGTH_FIELD]: false,
        [OutsideInfoEnum.PAVEMENT_TYPE_FIELD]: false,
      },
      [OutsideInfoEnum.FACADE_SUBTITLE]: {
        [OutsideInfoEnum.CHARACTER_FACADE_FIELD]: false,
        [OutsideInfoEnum.ELECTRICAL_CABLE_FIELD]: false,
        [OutsideInfoEnum.COAX_CABLE_FIELD]: false,
        [OutsideInfoEnum.MULTITAB_FIELD]: false,
        [OutsideInfoEnum.PUBLIC_LIGHTING]: false,
        [OutsideInfoEnum.FACADE_DOUBTS_FIELD]: false,
        [OutsideInfoEnum.SOLUTION_DOUBT_FIELD]: false,
      },
      [OutsideInfoEnum.STREET_SUBTITLE]: {
        [OutsideInfoEnum.STREET_FURNITURE_QUESTION_FIELD]: false,
        [OutsideInfoEnum.STREET_FURNITURE_FIELD]: false,
        [OutsideInfoEnum.FACADE_OBSTACLES_QUESTION_FIELD]: false,
        [OutsideInfoEnum.FACADE_OBSTACLES_FIELD]: false,
        [OutsideInfoEnum.STREET_PAVEMENT_FIELD]: false,
      },
      [OutsideInfoEnum.FRONT_YARD_PICTURES_SUBTITLE]: {
        [OutsideInfoEnum.FRONT_YARD_PICTURES_FIELD]: false,
      },
    });
  }

  private onChangeMainDistribution(mainDistribution: MainDistributionEnum) {
    if (mainDistribution === MainDistributionEnum.Facade) {
      const validators = [Validators.required, Validators.pattern("^[0-9]*$")];
      const mainDistributionHeightControl = new FormControl(
        this.survey.mainDistributionHeight,
        validators
      );
      this.technicalForm.addControl(
        "mainDistributionHeight",
        mainDistributionHeightControl
      );
      mainDistributionHeightControl.patchValue(
        this.survey.mainDistributionHeight != null
          ? this.survey.mainDistributionHeight
          : 3
      );
      this.technicalForm
        .get("mainDistributionUndergroundReason")
        .clearValidators();
      this.technicalForm.patchValue({
        mainDistributionUndergroundReason: null,
      });
    } else if (mainDistribution === MainDistributionEnum.Underground) {
      this.technicalForm
        .get("mainDistributionUndergroundReason")
        .addValidators(Validators.required);
      this.technicalForm.patchValue({
        mainDistributionUndergroundReason:
          this.survey.mainDistributionUndergroundReason,
      });
      this.technicalForm.removeControl("mainDistributionHeight");
    }
  }

  isClassRequired(): boolean {
    if (this.survey.building.buildingType != BuildingTypeEnum.NotInUse)
      return true;
    else return false;
  }

  setQuestions(): void {
    for (const key in this.survey)
      if (this.survey.hasOwnProperty(key))
        switch (key) {
          case "patrimony":
            this.setQuestion(key);
            break;
          case "solutionDoubt":
            this.setQuestion(key);
            break;
          case "electricityCables":
            this.setQuestion(key);
            break;
          case "coaxCables":
            this.setQuestion(key);
            break;
          case "multiTap":
            this.setQuestion(key);
            break;
          case "publicLightning":
            this.setQuestion(key);
            break;
          case "streetFurniture":
            this.setQuestion(key);
            break;
          case "otherObstruction":
            this.setQuestion(key);
            break;
          case "frontYard":
            this.setQuestion(key);
            break;
          case "generalBuildingRemark":
            this.remarks[key] = this.survey[key];
            break;
          case "facadeRemark":
            this.remarks[key] = this.survey[key];
            break;
          case "streetRemark":
            this.remarks[key] = this.survey[key];
            break;
        }
  }

  private setQuestion(key): void {
    this.questions[key] = {
      value: this.survey[key],
      error: false,
    };
  }

  onAnswer(key: string, answer: boolean): void {
    if (!this.isAuthCanWrite) return;

    if (key === "frontYard") this.toggleFrontYardItemsVisibility(answer);

    if (answer !== this.questions[key].value) {
      if (key === "streetFurniture")
        this.toggleStreetFurnitureItemsVisibility(answer);

      if (key === "otherObstruction")
        this.toggleOtherObstructionItemsVisibility(answer);

      if (key === "solutionDoubt")
        this.togglesolutionDoubtItemsVisibility(answer);
    }
    // eslint-disable-next-line prettier/prettier
    if (this.questions.hasOwnProperty(key)) this.questions[key].value = answer;
  }

  toggleFrontYardItemsVisibility(visible: boolean) {
    if (visible) {
      const mainDistributionFrontYardLengthControl = new FormControl(
        this.survey.mainDistributionFrontYardLength,
        [Validators.required, Validators.pattern("^[0-9]*$")]
      );
      this.technicalForm.addControl(
        "mainDistributionFrontYardLength",
        mainDistributionFrontYardLengthControl
      );
      this.technicalForm.patchValue({
        mainDistributionFrontYardLength:
          this.survey.mainDistributionFrontYardLength,
      });
      var mainDistributionFrontYardPavement = this.technicalForm.get(
        "mainDistributionFrontYardPavement"
      );
      mainDistributionFrontYardPavement.addValidators(Validators.required);
      this.technicalForm.patchValue({
        mainDistributionFrontYardPavement:
          this.survey.mainDistributionFrontYardPavement,
      });
    } else {
      var mainDistributionFrontYardLengthControl = this.technicalForm.get(
        "mainDistributionFrontYardLength"
      );
      if (mainDistributionFrontYardLengthControl)
        this.survey.mainDistributionFrontYardLength =
          mainDistributionFrontYardLengthControl.value;

      var mainDistributionFrontYardPavementControl = this.technicalForm.get(
        "mainDistributionFrontYardPavement"
      );
      if (mainDistributionFrontYardPavementControl)
        this.survey.mainDistributionFrontYardPavement =
          mainDistributionFrontYardPavementControl.value;

      this.technicalForm.removeControl("mainDistributionFrontYardLength");
      this.technicalForm
        .get("mainDistributionFrontYardPavement")
        .clearValidators();
    }
  }

  toggleStreetFurnitureItemsVisibility(visible: boolean) {
    if (visible) {
      const mainDistributionFrontYardPavementControl = new FormControl(
        this.survey.streetFurnitureTypes,
        [Validators.required]
      );
      this.technicalForm.addControl(
        "streetFurnitureTypes",
        mainDistributionFrontYardPavementControl
      );
    } else {
      this.technicalForm.removeControl("streetFurnitureTypes");
    }
  }

  toggleOtherObstructionItemsVisibility(visible: boolean) {
    if (visible) {
      const mainDistributionFrontYardPavementControl = new FormControl(
        this.survey.otherObstructionTypes,
        [Validators.required]
      );
      this.technicalForm.addControl(
        "otherObstructionTypes",
        mainDistributionFrontYardPavementControl
      );
    } else {
      this.technicalForm.removeControl("otherObstructionTypes");
    }
  }

  togglesolutionDoubtItemsVisibility(visible: boolean) {
    if (visible) {
      const solutionDoubtRemarkControl = new FormControl(
        this.survey.solutionDoubtRemark,
        [Validators.required]
      );
      this.technicalForm.addControl(
        "solutionDoubtRemark",
        solutionDoubtRemarkControl
      );
    } else {
      this.technicalForm.removeControl("solutionDoubtRemark");
    }
  }
  validate(): boolean {
    return this.isValid();
  }
  save(checkValidity?: boolean): Observable<any> {
    if (!this.isAuthCanWrite) return of(true);

    if (checkValidity && !this.isValid()) return of(false);

    if (this.questions.frontYard.value === false) {
      this.survey.mainDistributionFrontYardLength = null;
      this.survey.mainDistributionFrontYardPavement = null;
      this.technicalForm.patchValue({
        mainDistributionFrontYardPavement:
          this.survey.mainDistributionFrontYardPavement,
      });
    }

    if (this.isChanged()) {
      var remarks = this.survey.photos.frontYardPhotos
        .concat(this.survey.photos.solutionDoubtPhotos)
        .map((f) => {
          f.remarkChanged = false;
          return new FacadeDetailPhotoRemarksDto(
            f.data.id,
            f.facadeDetailPhotoRemark
          );
        });

      return this._savingStateService
        .addOutsideSurveyTechnical(this.refactorDataForServer(remarks))
        .pipe(
          tap(() => {
            this.survey.mainDistribution =
              this.technicalForm.get("mainDistribution").value;
          })
        );
    } else {
      return of(true);
    }
  }

  isChanged(): boolean {
    for (const key in this.questions)
      if (this.questions[key].value !== this.survey[key]) return true;

    for (const key in this.remarks)
      if (this.remarks[key] !== this.survey[key]) return true;

    const photoRemarksNow = this.frontYardPhotos
      .filter((p) => p.remarkChanged)
      .concat(this.solutionDoubtPhotos.filter((p) => p.remarkChanged))
      .map((p) => p.facadeDetailPhotoRemark);
    if (
      !photoRemarksNow.every((pr) =>
        this.frontYardPhotoRemarksOnInit
          .concat(this.frontYardPhotoRemarksOnInit)
          .includes(pr)
      )
    ) {
      this.frontYardPhotoRemarksOnInit = photoRemarksNow;
      return true;
    }

    if (
      this.survey.mainDistribution !==
      this.technicalForm.get("mainDistribution").value
    )
      return true;

    if (
      this.survey.pavingFootpath !==
      this.technicalForm.get("streetPavingType").value
    )
      return true;

    if (
      this.technicalForm.get("mainDistributionHeight") &&
      this.survey.mainDistributionHeight !==
        this.technicalForm.get("mainDistributionHeight").value
    )
      return true;

    if (
      this.technicalForm.get("solutionDoubtRemark") &&
      this.survey.solutionDoubtRemark !==
        this.technicalForm.get("solutionDoubtRemark").value
    )
      return true;

    if (
      this.technicalForm.get("mainDistributionUndergroundReason") &&
      this.survey.mainDistributionUndergroundReason !==
        this.technicalForm.get("mainDistributionUndergroundReason").value
    )
      return true;

    if (
      this.technicalForm.get("mainDistributionFrontYardLength") &&
      this.survey.mainDistributionFrontYardLength !==
        this.technicalForm.get("mainDistributionFrontYardLength").value
    )
      return true;

    if (
      this.technicalForm.get("mainDistributionFrontYardPavement") &&
      this.survey.mainDistributionFrontYardPavement !==
        this.technicalForm.get("mainDistributionFrontYardPavement").value
    )
      return true;

    if (
      this.technicalForm.get("streetFurnitureTypes") &&
      !isEqual(
        this.survey.streetFurnitureTypes,
        this.technicalForm.get("streetFurnitureTypes").value
      )
    )
      return true;

    if (
      this.technicalForm.get("otherObstructionTypes") &&
      !isEqual(
        this.survey.otherObstructionTypes,
        this.technicalForm.get("otherObstructionTypes").value
      )
    )
      return true;

    return false;
  }

  private isValid(): boolean {
    this.formValidationService.resetErrorsForPath(OUTSIDE_SURVEY_OUTSIDE_INFO);
    let isValid = true;

    if (this.survey.building.buildingType == BuildingTypeEnum.NotInUse)
      return true;

    if (!this.technicalForm.valid) {
      Object.keys(this.technicalForm.controls).forEach((field) => {
        const control = this.technicalForm.get(field);
        control.markAsTouched({ onlySelf: true });
      });
      let control = this.technicalForm.get(OutsideInfoEnum.TYPE_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE,
          OutsideInfoEnum.TYPE_FIELD
        );

      control = this.technicalForm.get(OutsideInfoEnum.FACADE_HEIGHT_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE,
          OutsideInfoEnum.FACADE_HEIGHT_FIELD
        );

      control = this.technicalForm.get(OutsideInfoEnum.FRONT_YARD_LENGTH_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE,
          OutsideInfoEnum.FRONT_YARD_LENGTH_FIELD
        );

      control = this.technicalForm.get(OutsideInfoEnum.PAVEMENT_TYPE_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE,
          OutsideInfoEnum.PAVEMENT_TYPE_FIELD
        );

      control = this.technicalForm.get(OutsideInfoEnum.SOLUTION_DOUBT_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.FACADE_SUBTITLE,
          OutsideInfoEnum.FACADE_DOUBTS_FIELD
        );

      control = this.technicalForm.get(OutsideInfoEnum.FACADE_OBSTACLES_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.STREET_SUBTITLE,
          OutsideInfoEnum.FACADE_OBSTACLES_FIELD
        );

      control = this.technicalForm.get(OutsideInfoEnum.STREET_FURNITURE_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.STREET_SUBTITLE,
          OutsideInfoEnum.STREET_FURNITURE_FIELD
        );

      control = this.technicalForm.get(OutsideInfoEnum.STREET_PAVEMENT_FIELD);
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.STREET_SUBTITLE,
          OutsideInfoEnum.STREET_PAVEMENT_FIELD
        );

      control = this.technicalForm.get(
        OutsideInfoEnum.UNDERGROUND_REASON_FIELD
      );
      if (control?.invalid)
        this.formValidationService.addError(
          OUTSIDE_SURVEY_OUTSIDE_INFO,
          OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE,
          OutsideInfoEnum.UNDERGROUND_REASON_FIELD
        );

      this.toastService.showWarnToast(
        "Warning",
        "Please fill out all the questions."
      );
      isValid = false;
    }

    if (
      this.technicalForm.get(OutsideInfoEnum.TYPE_FIELD).value ==
        MainDistributionEnum.Facade &&
      this.survey.building.buildingType == BuildingTypeEnum.EmptyParcel
    ) {
      isValid = false;
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE,
        OutsideInfoEnum.TYPE_FIELD
      );
    }

    if (this.questions.frontYard.value && this.frontYardPhotos.length === 0) {
      this.toastService.showWarnToast(
        "Warning",
        "At least 1 frontyard photo is required."
      );
      isValid = false;
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.FRONT_YARD_PICTURES_SUBTITLE,
        OutsideInfoEnum.FRONT_YARD_PICTURES_FIELD
      );
    }
    for (const key in this.questions)
      if (
        this.questions.hasOwnProperty(key) &&
        typeof this.questions[key].value !== "boolean"
      ) {
        this.questions[key].error = true;
        this.toastService.showWarnToast(
          "Warning",
          "Please fill out all the questions."
        );
        isValid = false;
      } else {
        this.questions[key].error = false;
      }

    if (this.questions[OutsideInfoEnum.FRONT_YARD_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.MAIN_DISTRIBUTION_SUBTITLE,
        OutsideInfoEnum.FRONT_YARD_FIELD
      );

    if (this.questions[OutsideInfoEnum.CHARACTER_FACADE_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.FACADE_SUBTITLE,
        OutsideInfoEnum.CHARACTER_FACADE_FIELD
      );

    if (this.questions[OutsideInfoEnum.ELECTRICAL_CABLE_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.FACADE_SUBTITLE,
        OutsideInfoEnum.ELECTRICAL_CABLE_FIELD
      );

    if (this.questions[OutsideInfoEnum.COAX_CABLE_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.FACADE_SUBTITLE,
        OutsideInfoEnum.COAX_CABLE_FIELD
      );

    if (this.questions[OutsideInfoEnum.MULTITAB_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.FACADE_SUBTITLE,
        OutsideInfoEnum.MULTITAB_FIELD
      );

    if (this.questions[OutsideInfoEnum.PUBLIC_LIGHTING].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.FACADE_SUBTITLE,
        OutsideInfoEnum.PUBLIC_LIGHTING
      );

    if (this.questions[OutsideInfoEnum.FACADE_DOUBTS_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.FACADE_SUBTITLE,
        OutsideInfoEnum.FACADE_DOUBTS_FIELD
      );

    if (this.questions[OutsideInfoEnum.STREET_FURNITURE_QUESTION_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.STREET_SUBTITLE,
        OutsideInfoEnum.STREET_FURNITURE_QUESTION_FIELD
      );

    if (this.questions[OutsideInfoEnum.FACADE_OBSTACLES_QUESTION_FIELD].error)
      this.formValidationService.addError(
        OUTSIDE_SURVEY_OUTSIDE_INFO,
        OutsideInfoEnum.STREET_SUBTITLE,
        OutsideInfoEnum.FACADE_OBSTACLES_QUESTION_FIELD
      );

    return isValid;
  }

  getErrors(address?: string, subTitle?: string, field?: string) {
    return this.formValidationService.getErrors(
      ...[OUTSIDE_SURVEY_OUTSIDE_INFO, address, subTitle, field].filter(
        (x) => !!x
      )
    );
  }

  private refactorDataForServer(
    frontYardPhotoRemarks: FacadeDetailPhotoRemarksDto[]
  ) {
    let data = {};
    for (const key in this.questions)
      if (this.questions.hasOwnProperty(key))
        data[key] = this.questions[key].value;

    data = {
      id: this.survey.id,
      mainDistribution: this.technicalForm.get("mainDistribution").value,
      mainDistributionHeight: this.technicalForm.get("mainDistributionHeight")
        ? this.technicalForm.get("mainDistributionHeight").value
        : null,
      mainDistributionUndergroundReason: this.technicalForm.get(
        "mainDistributionUndergroundReason"
      )
        ? this.technicalForm.get("mainDistributionUndergroundReason").value
        : null,
      mainDistributionFrontYardLength: this.technicalForm.get(
        "mainDistributionFrontYardLength"
      )
        ? this.technicalForm.get("mainDistributionFrontYardLength").value
        : null,
      mainDistributionFrontYardPavement: this.technicalForm.get(
        "mainDistributionFrontYardPavement"
      )
        ? this.technicalForm.get("mainDistributionFrontYardPavement").value
        : null,
      streetFurnitureTypes: this.technicalForm.get("streetFurnitureTypes")
        ? this.technicalForm.get("streetFurnitureTypes").value
        : null,
      otherObstructionTypes: this.technicalForm.get("otherObstructionTypes")
        ? this.technicalForm.get("otherObstructionTypes").value
        : null,

      pavingFootpath: this.technicalForm.get("streetPavingType")
        ? this.technicalForm.get("streetPavingType").value
        : null,
      ...data,
      ...this.remarks,
      solutionDoubtRemark: this.technicalForm.get("solutionDoubtRemark")
        ? this.technicalForm.get("solutionDoubtRemark").value
        : null,
      frontYardPhotoRemarks: frontYardPhotoRemarks,
    };

    this.survey = merge(this.survey, data);
    return data;
  }

  private updateSurvey(): void {
    for (const key in this.questions)
      if (this.questions.hasOwnProperty(key) && this.survey.hasOwnProperty(key))
        this.survey[key] = this.questions[key].value;

    for (const key in this.remarks)
      if (this.remarks.hasOwnProperty(key) && this.survey.hasOwnProperty(key))
        this.survey[key] = this.remarks[key];

    this.survey.mainDistribution =
      this.technicalForm.get("mainDistribution").value;
    this.survey.pavingFootpath =
      this.technicalForm.get("streetPavingType").value;
    if (this.technicalForm.get("mainDistributionHeight"))
      this.survey.mainDistributionHeight = this.technicalForm.get(
        "mainDistributionHeight"
      ).value;

    if (this.technicalForm.get("mainDistributionUndergroundReason"))
      this.survey.mainDistributionUndergroundReason = this.technicalForm.get(
        "mainDistributionUndergroundReason"
      ).value;

    if (this.technicalForm.get("mainDistributionFrontYardLength"))
      this.survey.mainDistributionFrontYardLength = this.technicalForm.get(
        "mainDistributionFrontYardLength"
      ).value;

    if (this.technicalForm.get("mainDistributionFrontYardPavement"))
      this.survey.mainDistributionFrontYardPavement = this.technicalForm.get(
        "mainDistributionFrontYardPavement"
      ).value;

    if (this.technicalForm.get("streetFurnitureTypes"))
      this.survey.streetFurnitureTypes = this.technicalForm.get(
        "streetFurnitureTypes"
      ).value;

    if (this.technicalForm.get("otherObstructionTypes"))
      this.survey.otherObstructionTypes = this.technicalForm.get(
        "otherObstructionTypes"
      ).value;
  }

  getMainAddressId() {
    return this.survey.building.addresses.find((a) => a.isMainAddress).id;
  }
}
